import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { BOARDS, GRADES } from "@/lib/nep";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function EditProfile() {
  const { user, updateProfile } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", grade: "", education_board: "", school_name: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        grade: user.grade || "",
        education_board: user.education_board || "",
        school_name: user.school_name || "",
      });
    }
  }, [user]);

  if (!user) return null;

  const showSchoolField = ["student", "counselor", "principal"].includes(user.role);
  const showGradeBoard = user.role === "student";

  const submit = async (e) => {
    e.preventDefault();
    setErr("");

    const isChangingGrade =
      showGradeBoard && user.grade && form.grade && String(form.grade) !== String(user.grade);
    if (isChangingGrade) {
      const confirmed = window.confirm(
        "You are changing your class. Since assessment questions are tailored to each class, your current assessment progress will be cleared and you will need to start over from the beginning. Do you want to continue?"
      );
      if (!confirmed) return;
    }

    setLoading(true);
    try {
      const payload = { name: form.name };
      if (showGradeBoard) {
        payload.grade = form.grade || undefined;
        payload.education_board = form.education_board || undefined;
      }
      if (showSchoolField) {
        payload.school_name = form.school_name || undefined;
      }
      await updateProfile(payload);
      toast.success(isChangingGrade ? "Profile updated. Your assessment has been restarted for your new class." : "Profile updated");
      nav(-1);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="card-brutal p-8">
          <span className="label-mono">Account</span>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold tracking-tighter mt-2">Edit profile</h1>
          <p className="text-sm text-[#52525B] mt-2">Update your details below.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label-mono block mb-2">Full name</label>
                <input
                  data-testid="profile-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="label-mono block mb-2">Email</label>
                <input disabled value={user.email} className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-[#F4F4F5] text-[#52525B]" />
              </div>
            </div>

            {showGradeBoard && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label-mono block mb-2">Class / Grade</label>
                  <select
                    data-testid="profile-grade"
                    value={form.grade}
                    onChange={(e) => setForm({ ...form, grade: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select class</option>
                    {GRADES.map((g) => <option key={g} value={g}>Class {g}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono block mb-2">Education Board</label>
                  <select
                    data-testid="profile-board"
                    value={form.education_board}
                    onChange={(e) => setForm({ ...form, education_board: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select board</option>
                    {BOARDS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
            )}

            {showSchoolField && (
              <div>
                <label className="label-mono block mb-2">School</label>
                <input
                  data-testid="profile-school"
                  value={form.school_name}
                  onChange={(e) => setForm({ ...form, school_name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {err && <div data-testid="profile-error" className="text-sm text-red-700 bg-red-100 border-2 border-red-700 rounded-lg px-3 py-2">{err}</div>}

            <div className="flex items-center gap-3">
              <button type="submit" data-testid="profile-save" disabled={loading} className="btn-brutal bg-blue-600 text-white px-6 py-3 flex items-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="animate-spin" size={16} />} Save changes
              </button>
              <button type="button" onClick={() => nav(-1)} className="btn-brutal bg-white px-6 py-3">
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
